import { remaining, type Remaining } from '@ovrsee/shared'
import { useEffect, useState } from 'react'

export let serverClockOffsetMs = 0

/**
 * Corrects for a skewed client clock. Countdowns are computed against the server's idea
 * of the time, taken from the Date response header, so a laptop an hour out does not show
 * the wrong time remaining on every task.
 */
export function setServerOffsetFromResponse(dateHeader: string | null): void {
  if (dateHeader === null) {
    serverClockOffsetMs = 0
    return
  }
  const serverMs = Date.parse(dateHeader)
  if (Number.isNaN(serverMs)) return
  serverClockOffsetMs = serverMs - Date.now()
}

export function getServerOffsetMs(): number {
  return serverClockOffsetMs
}

export function now(): Date {
  return new Date(Date.now() + serverClockOffsetMs)
}

/**
 * One ticker for the whole app, not one per component.
 *
 * This ticks once a SECOND. An earlier version deliberately ticked once a minute because
 * the display was days-and-hours and per-second re-rendering bought nothing visible. The
 * owner asked for a visibly counting clock on the employee cards (M12), so that reasoning
 * no longer applies - the seconds are the point. The cost is controlled by sharing one
 * interval across every subscriber instead of by ticking slowly.
 */
const subscribers = new Set<() => void>()
let ticker: ReturnType<typeof setInterval> | null = null

function subscribe(fn: () => void): () => void {
  subscribers.add(fn)
  if (ticker === null) {
    ticker = setInterval(() => {
      for (const notify of subscribers) notify()
    }, 1000)
  }
  return () => {
    subscribers.delete(fn)
    if (subscribers.size === 0 && ticker !== null) {
      clearInterval(ticker)
      ticker = null
    }
  }
}

export function useCountdown(deadlineIso: string): Remaining {
  const compute = () => remaining(new Date(deadlineIso), now())
  const [value, setValue] = useState<Remaining>(compute)

  useEffect(() => {
    setValue(compute())
    return subscribe(() => setValue(compute()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineIso])

  return value
}
